import { useRef, useState } from 'react'
import { tokenSpeicher } from '../api/client'

type Art = 'FAHRER' | 'FAHRZEUGE'

interface Feldangabe {
  schluessel: string
  beschriftung: string
  pflicht: boolean
  hinweis: string | null
}

interface Analyse {
  art: string
  blattname: string
  ueberschriften: string[]
  felder: Feldangabe[]
  vorschlag: Record<string, number>
  beispielzeilen: string[][]
  anzahlDatenzeilen: number
  nichtZugeordnet: string[]
}

type Befund = 'NEU' | 'AKTUALISIERUNG' | 'UEBERSPRUNGEN'

interface Zeilenbefund {
  zeile: number
  bezeichnung: string
  befund: Befund
  werte: Record<string, string>
  hinweise: string[]
}

interface Ergebnis {
  art: string
  nurVorschau: boolean
  angelegt: number
  aktualisiert: number
  uebersprungen: number
  zeilen: Zeilenbefund[]
  zeilenGekuerzt: boolean
}

type Zuweisung = Record<string, number | null>

const ARTEN: { schluessel: Art; titel: string; hinweis: string }[] = [
  {
    schluessel: 'FAHRER',
    titel: 'Fahrpersonal',
    hinweis: 'Die Personalnummer ist der Schlüssel: Dieselbe Nummer aktualisiert den vorhandenen Mitarbeiter.',
  },
  {
    schluessel: 'FAHRZEUGE',
    titel: 'Fahrzeuge',
    hinweis: 'Das Kennzeichen ist der Schlüssel: Dasselbe Kennzeichen aktualisiert das vorhandene Fahrzeug.',
  },
]

/** Spaltenbuchstabe wie in Excel: 0 → A, 25 → Z, 26 → AA. */
function spaltenname(index: number): string {
  let name = ''
  let rest = index
  do {
    name = String.fromCharCode(65 + (rest % 26)) + name
    rest = Math.floor(rest / 26) - 1
  } while (rest >= 0)
  return name
}

/** Multipart-Aufrufe mit Token — der API-Client kennt keine zusätzlichen Formularfelder. */
async function sende<T>(
  pfad: string,
  datei: File,
  felder: Record<string, string> = {},
): Promise<T> {
  const formular = new FormData()
  formular.append('datei', datei)
  Object.entries(felder).forEach(([name, wert]) => formular.append(name, wert))
  const antwort = await fetch(pfad, {
    method: 'POST',
    headers: { Authorization: `Bearer ${tokenSpeicher.access()}` },
    body: formular,
  })
  if (!antwort.ok) {
    const koerper = await antwort.json().catch(() => ({}))
    throw new Error(koerper.message || `Fehler ${antwort.status}`)
  }
  return antwort.json()
}

/**
 * Übernahme bestehender Listen beim Einrichten eines Mandanten.
 *
 * Drei Schritte: Datei wählen, Spalten zuordnen, Vorschau prüfen. Erst danach wird
 * geschrieben. Die Spaltenerkennung liefert nur einen Vorschlag — verbindlich ist, was hier
 * bestätigt wird.
 */
export function StammdatenImport() {
  const [art, setArt] = useState<Art>('FAHRER')
  const [datei, setDatei] = useState<File | null>(null)
  const [analyse, setAnalyse] = useState<Analyse | null>(null)
  const [zuweisung, setZuweisung] = useState<Zuweisung>({})
  const [vorschau, setVorschau] = useState<Ergebnis | null>(null)
  const [uebernommen, setUebernommen] = useState<Ergebnis | null>(null)
  const [laeuft, setLaeuft] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [nurProbleme, setNurProbleme] = useState(false)
  const feld = useRef<HTMLInputElement | null>(null)

  function zuruecksetzen() {
    setDatei(null)
    setAnalyse(null)
    setZuweisung({})
    setVorschau(null)
    setUebernommen(null)
    setFehler(null)
    if (feld.current) feld.current.value = ''
  }

  function artWechseln(neu: Art) {
    setArt(neu)
    zuruecksetzen()
  }

  async function dateiLesen(gewaehlt: File | undefined) {
    if (!gewaehlt) return
    setDatei(gewaehlt)
    setAnalyse(null)
    setVorschau(null)
    setUebernommen(null)
    setFehler(null)
    setLaeuft('analyse')
    try {
      const antwort = await sende<Analyse>('/api/stammdaten/import/analyse', gewaehlt, { art })
      setAnalyse(antwort)
      const vorbelegt: Zuweisung = {}
      antwort.felder.forEach((f) => {
        vorbelegt[f.schluessel] = antwort.vorschlag[f.schluessel] ?? null
      })
      setZuweisung(vorbelegt)
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Datei konnte nicht gelesen werden')
      setDatei(null)
    } finally {
      setLaeuft(null)
    }
  }

  function zuordnen(schluessel: string, spalte: number | null) {
    setZuweisung((alt) => ({ ...alt, [schluessel]: spalte }))
    // Jede Änderung entwertet die Vorschau — sie stünde sonst für eine andere Zuordnung.
    setVorschau(null)
  }

  async function rechnen(pfad: 'vorschau' | 'uebernehmen') {
    if (!datei) return
    setLaeuft(pfad)
    setFehler(null)
    try {
      const antwort = await sende<Ergebnis>(`/api/stammdaten/import/${pfad}`, datei, {
        art,
        zuweisung: JSON.stringify(zuweisung),
      })
      if (pfad === 'vorschau') {
        setVorschau(antwort)
      } else {
        setUebernommen(antwort)
        setVorschau(null)
      }
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Verarbeitung fehlgeschlagen')
    } finally {
      setLaeuft(null)
    }
  }

  async function vorlage() {
    try {
      const antwort = await fetch(`/api/stammdaten/import/vorlage/${art.toLowerCase()}`, {
        headers: { Authorization: `Bearer ${tokenSpeicher.access()}` },
      })
      if (!antwort.ok) throw new Error(`Fehler ${antwort.status}`)
      const blob = await antwort.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `vlzhub-vorlage-${art.toLowerCase()}.xlsx`
      link.click()
      URL.revokeObjectURL(url)
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Vorlage konnte nicht geladen werden')
    }
  }

  // Dieselbe Spalte zweimal zuzuordnen ist fast immer ein Versehen.
  const doppelt = new Set(
    Object.values(zuweisung)
      .filter((s): s is number => s !== null)
      .filter((s, i, alle) => alle.indexOf(s) !== i),
  )
  const pflichtOffen = (analyse?.felder ?? [])
    .filter((f) => f.pflicht && zuweisung[f.schluessel] == null)
    .map((f) => f.beschriftung)

  const gezeigteZeilen = (vorschau?.zeilen ?? []).filter(
    (z) => !nurProbleme || z.befund === 'UEBERSPRUNGEN' || z.hinweise.length > 0,
  )
  const mitHinweisen = (vorschau?.zeilen ?? []).filter(
    (z) => z.befund === 'UEBERSPRUNGEN' || z.hinweise.length > 0,
  ).length

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Stammdaten übernehmen</h1>
          <p>
            Fahrpersonal und Fahrzeugbestand aus vorhandenen Excel-Listen einlesen — mit
            Spaltenzuordnung und Vorschau, bevor etwas geschrieben wird.
          </p>
        </div>
        <div className="werkzeugleiste">
          <button className="knopf-schlicht" onClick={() => void vorlage()}>
            Vorlage herunterladen
          </button>
          {datei && (
            <button className="knopf-schlicht" onClick={zuruecksetzen}>
              Neu beginnen
            </button>
          )}
        </div>
      </div>

      {fehler && <div className="meldung fehler">{fehler}</div>}

      {/* Schritt 1 ------------------------------------------------------ */}
      <div className="karte abschnitt">
        <h2>1 · Was soll übernommen werden?</h2>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          {ARTEN.map((a) => (
            <button
              key={a.schluessel}
              className={art === a.schluessel ? 'knopf-primaer' : 'knopf-schlicht'}
              onClick={() => artWechseln(a.schluessel)}
            >
              {a.titel}
            </button>
          ))}
        </div>
        <p style={{ color: 'var(--grau-700)', fontSize: '0.88rem', lineHeight: 1.6 }}>
          {ARTEN.find((a) => a.schluessel === art)?.hinweis} Ein erneuter Import derselben Liste
          legt also nichts doppelt an.
        </p>

        <input
          ref={feld}
          type="file"
          accept=".xls,.xlsx"
          style={{ display: 'none' }}
          onChange={(e) => void dateiLesen(e.target.files?.[0])}
        />
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            className="knopf-akzent"
            disabled={laeuft !== null}
            onClick={() => feld.current?.click()}
          >
            {laeuft === 'analyse' ? 'Wird gelesen …' : 'Excel-Datei auswählen'}
          </button>
          {datei && (
            <span style={{ color: 'var(--grau-700)', fontSize: '0.88rem' }}>
              {datei.name}
              {analyse && (
                <>
                  {' · '}Blatt „{analyse.blattname}" · {analyse.anzahlDatenzeilen} Datenzeilen
                </>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Schritt 2 ------------------------------------------------------ */}
      {analyse && (
        <div className="karte abschnitt">
          <h2>2 · Spalten zuordnen</h2>
          <p style={{ color: 'var(--grau-700)', fontSize: '0.88rem', lineHeight: 1.6 }}>
            Die Zuordnung wurde aus den Überschriften vorgeschlagen. Prüfen Sie sie — maßgeblich
            ist, was hier steht, nicht die Überschrift in der Datei. Neben jeder Auswahl steht der
            Wert aus der ersten Datenzeile.
          </p>

          {analyse.nichtZugeordnet.length > 0 && (
            <div className="meldung hinweis">
              Diese Spalten der Datei wurden keinem Feld zugeordnet und bleiben unberücksichtigt:{' '}
              <strong>{analyse.nichtZugeordnet.join(', ')}</strong>
            </div>
          )}
          {doppelt.size > 0 && (
            <div className="meldung fehler">
              Dieselbe Spalte ist mehrfach zugeordnet:{' '}
              <strong>
                {[...doppelt].map((s) => `${spaltenname(s)} · ${analyse.ueberschriften[s]}`).join(', ')}
              </strong>
              . Das ist möglich, aber selten gewollt.
            </div>
          )}

          <div className="tabelle-huelle">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '30%' }}>Feld</th>
                  <th style={{ width: '35%' }}>Spalte in der Datei</th>
                  <th>Beispielwert</th>
                </tr>
              </thead>
              <tbody>
                {analyse.felder.map((f) => {
                  const spalte = zuweisung[f.schluessel]
                  const beispiel =
                    spalte != null && analyse.beispielzeilen.length > 0
                      ? analyse.beispielzeilen[0][spalte] || ''
                      : ''
                  return (
                    <tr key={f.schluessel}>
                      <td>
                        <strong>{f.beschriftung}</strong>
                        {f.pflicht && (
                          <span style={{ color: 'var(--rot-600, #c0392b)' }}> *</span>
                        )}
                        {f.hinweis && (
                          <div style={{ color: 'var(--grau-500)', fontSize: '0.78rem', marginTop: 2 }}>
                            {f.hinweis}
                          </div>
                        )}
                      </td>
                      <td>
                        <select
                          className="feld"
                          value={spalte ?? ''}
                          onChange={(e) =>
                            zuordnen(f.schluessel, e.target.value === '' ? null : Number(e.target.value))
                          }
                          style={
                            spalte != null && doppelt.has(spalte)
                              ? { borderColor: 'var(--rot-600, #c0392b)' }
                              : undefined
                          }
                        >
                          <option value="">— nicht übernehmen —</option>
                          {analyse.ueberschriften.map((u, i) =>
                            u ? (
                              <option key={i} value={i}>
                                {spaltenname(i)} · {u}
                              </option>
                            ) : null,
                          )}
                        </select>
                      </td>
                      <td style={{ color: 'var(--grau-700)' }}>{beispiel}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {pflichtOffen.length > 0 && (
            <div className="meldung fehler" style={{ marginTop: 14 }}>
              Ohne diese Zuordnung ist keine Übernahme möglich:{' '}
              <strong>{pflichtOffen.join(', ')}</strong>
            </div>
          )}

          <div style={{ marginTop: 14 }}>
            <button
              className="knopf-primaer"
              disabled={laeuft !== null || pflichtOffen.length > 0}
              onClick={() => void rechnen('vorschau')}
            >
              {laeuft === 'vorschau' ? 'Wird geprüft …' : 'Vorschau erstellen'}
            </button>
          </div>
        </div>
      )}

      {/* Schritt 3 ------------------------------------------------------ */}
      {vorschau && (
        <div className="karte abschnitt">
          <h2>3 · Vorschau</h2>
          <div className="meldung hinweis">
            Bis hierher wurde <strong>nichts gespeichert</strong>. Die Übernahme wurde vollständig
            durchgerechnet und wieder verworfen — was unten steht, ist genau das, was beim
            Übernehmen geschieht.
          </div>

          <div className="karten-raster" style={{ marginBottom: 16 }}>
            <div className="kennzahl">
              <div className="zahl">{vorschau.angelegt}</div>
              <div className="etikett">werden neu angelegt</div>
            </div>
            <div className="kennzahl">
              <div className="zahl">{vorschau.aktualisiert}</div>
              <div className="etikett">werden aktualisiert</div>
            </div>
            <div className="kennzahl">
              <div className="zahl">{vorschau.uebersprungen}</div>
              <div className="etikett">werden übersprungen</div>
            </div>
            <div className="kennzahl">
              <div className="zahl">{mitHinweisen}</div>
              <div className="etikett">Zeilen mit Anmerkung</div>
            </div>
          </div>

          {mitHinweisen > 0 && (
            <label
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                marginBottom: 12,
                fontSize: '0.88rem',
                color: 'var(--grau-700)',
              }}
            >
              <input
                type="checkbox"
                checked={nurProbleme}
                onChange={(e) => setNurProbleme(e.target.checked)}
              />
              Nur Zeilen mit Anmerkungen zeigen
            </label>
          )}

          <div className="tabelle-huelle">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Zeile</th>
                  <th style={{ width: 130 }}>Vorgang</th>
                  <th>{art === 'FAHRER' ? 'Mitarbeiter' : 'Fahrzeug'}</th>
                  <th>Übernommene Angaben</th>
                  <th>Anmerkung</th>
                </tr>
              </thead>
              <tbody>
                {gezeigteZeilen.map((z) => (
                  <tr key={z.zeile}>
                    <td style={{ color: 'var(--grau-500)' }}>{z.zeile}</td>
                    <td>
                      <span
                        className={
                          z.befund === 'NEU'
                            ? 'meldung erfolg'
                            : z.befund === 'AKTUALISIERUNG'
                              ? 'meldung hinweis'
                              : 'meldung fehler'
                        }
                        style={{ padding: '2px 8px', fontSize: '0.76rem', display: 'inline-block' }}
                      >
                        {z.befund === 'NEU'
                          ? 'neu'
                          : z.befund === 'AKTUALISIERUNG'
                            ? 'Aktualisierung'
                            : 'übersprungen'}
                      </span>
                    </td>
                    <td>
                      <strong>{z.bezeichnung}</strong>
                    </td>
                    <td style={{ color: 'var(--grau-700)', fontSize: '0.82rem' }}>
                      {Object.entries(z.werte)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(' · ')}
                    </td>
                    <td style={{ color: 'var(--grau-700)', fontSize: '0.82rem' }}>
                      {z.hinweise.join(' · ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {vorschau.zeilenGekuerzt && (
            <p style={{ color: 'var(--grau-500)', fontSize: '0.82rem', marginTop: 10 }}>
              Die Liste zeigt die ersten 500 Zeilen. Die Zählwerte oben umfassen alle.
            </p>
          )}

          <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              className="knopf-primaer"
              disabled={laeuft !== null}
              onClick={() => void rechnen('uebernehmen')}
            >
              {laeuft === 'uebernehmen'
                ? 'Wird übernommen …'
                : `${vorschau.angelegt + vorschau.aktualisiert} Datensätze übernehmen`}
            </button>
            <button className="knopf-schlicht" onClick={() => setVorschau(null)}>
              Zurück zur Zuordnung
            </button>
          </div>
        </div>
      )}

      {/* Ergebnis ------------------------------------------------------- */}
      {uebernommen && (
        <div className="karte abschnitt">
          <h2>Übernommen</h2>
          <div className="meldung erfolg">
            <strong>{uebernommen.angelegt}</strong> angelegt ·{' '}
            <strong>{uebernommen.aktualisiert}</strong> aktualisiert
            {uebernommen.uebersprungen > 0 && (
              <>
                {' · '}
                <strong>{uebernommen.uebersprungen}</strong> übersprungen
              </>
            )}
          </div>
          <button className="knopf-schlicht" onClick={zuruecksetzen}>
            Weitere Liste einlesen
          </button>
        </div>
      )}

      {/* Hilfe ---------------------------------------------------------- */}
      {!datei && (
        <div className="karte">
          <h2>Reihenfolge bei der Einrichtung</h2>
          <ol style={{ lineHeight: 1.9, color: 'var(--grau-700)', paddingLeft: 20 }}>
            <li>
              <strong>Betriebshöfe</strong> anlegen — sie werden von Fahrern und Fahrzeugen
              referenziert.
            </li>
            <li>
              <strong>Kursblätter</strong> in der Kursbibliothek hochladen. Damit stehen die
              Kursnummern fest.
            </li>
            <li>
              <strong>Teams</strong> anlegen und ihnen Kurse zuordnen.
            </li>
            <li>
              <strong>Fahrpersonal und Fahrzeuge</strong> hier einlesen. Betriebshof, Team und
              Kurserfahrung werden über den Namen zugeordnet — deshalb erst jetzt.
            </li>
            <li>Sperren beim einzelnen Mitarbeiter nachtragen; sie lassen sich nicht importieren.</li>
          </ol>
        </div>
      )}
    </>
  )
}
